import { useTeacherScope } from '@/lib/useTeacherScope';
import { askConfirm, askPrompt } from '@/components/ui/ConfirmHost';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getClasses, getSubjects, getTerms, getSessions, getAssessmentComponents,
} from '@/api/owner';
import {
  getScores, postScores, postScoresLock,
} from '@/api/teacher';
import {
  Save, Lock, Unlock, AlertCircle, Check, Clipboard, Loader2, RefreshCw, MonitorCheck,
} from 'lucide-react';
import { CbtScoresModal } from '@/features/owner/components/CbtScoresModal';
import { useAuth } from '@/stores/auth';
import { api } from '@/lib/api';

const arr = <T,>(src: any): T[] => {
  if (!src) return [];
  if (Array.isArray(src)) return src;
  if (Array.isArray(src.data)) return src.data;
  if (Array.isArray(src.data?.data)) return src.data.data;
  return [];
};
const unwrap = (r: any) => r?.data?.data ?? r?.data;

export function ScoreEntryPage() {
  const qc = useQueryClient();
  const [sessionId, setSessionId] = useState<number | ''>('');
  const [classId, setClassId] = useState<number | ''>('');
  const [subjectId, setSubjectId] = useState<number | ''>('');
  const [termId, setTermId] = useState<number | ''>('');
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [cbtOpen, setCbtOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const role = useAuth((x: any) => x.user?.role);
  const canReopen = role === 'owner' || role === 'desk';

  const classesQ = useQuery({ queryKey: ['owner-classes'], queryFn: () => getClasses({ per_page: 200 }).then(unwrap) });
  const subjectsQ = useQuery({ queryKey: ['owner-subjects'], queryFn: () => getSubjects({ per_page: 500 }).then(unwrap) });
  const termsQ = useQuery({ queryKey: ['owner-terms'], queryFn: () => getTerms().then(unwrap) });
  const sessionsQ = useQuery({ queryKey: ['owner-sessions'], queryFn: () => getSessions().then(unwrap) });

  const scope = useTeacherScope();
  const classes = scope.classes(arr<any>(classesQ.data));
  const subjects = scope.subjects(arr<any>(subjectsQ.data), classId);
  const terms = arr<any>(termsQ.data);
  const sessions = arr<any>(sessionsQ.data);

  // Filter terms by selected session
  const visibleTerms = sessionId
    ? terms.filter((t: any) => (t.session_id ?? t.academic_session_id) === Number(sessionId))
    : terms;

  // Auto-pick current session on load
  useEffect(() => {
    if (!sessionId && sessions.length > 0) {
      const active = sessions.find((s: any) => s.is_current) ?? sessions[0];
      if (active) setSessionId(active.id);
    }
  }, [sessions, sessionId]);

  // Auto-pick current term within session
  useEffect(() => {
    if (!termId && visibleTerms.length > 0) {
      const current = visibleTerms.find((t: any) => t.is_current);
      if (current) setTermId(current.id);
    }
    // If current term no longer in visible session, clear
    if (termId && !visibleTerms.some((t: any) => t.id === termId)) {
      setTermId('');
    }
  }, [visibleTerms, termId]);

  const scoresQ = useQuery({
    queryKey: ['scores-grid', classId, subjectId, termId],
    queryFn: () => getScores({
      class_id: Number(classId),
      subject_id: Number(subjectId),
      term_id: Number(termId),
    }).then(unwrap),
    enabled: !!classId && !!subjectId && !!termId,
  });

  const data = scoresQ.data;
  const columns = arr<any>(data?.columns);
  const students = arr<any>(data?.students);
  const isLocked = !!data?.locked;
  const isPublished = !!data?.published;
  // students whose result is already published: their row is frozen; the others can still be scored
  const doneIds = new Set<number>(((data as any)?.published_student_ids ?? []).map(Number));

  // Reset drafts when grid changes
  useEffect(() => {
    setDraft({});
    setDirty(false);
    setSavedAt(null);
    setError(null);
  }, [classId, subjectId, termId, data?.class]);

  const cellKey = (studentId: number, componentId: number) => `${studentId}-${componentId}`;
  const valueOf = (studentId: number, componentId: number): string => {
    const k = cellKey(studentId, componentId);
    if (k in draft) return draft[k];
    const val = students.find((s: any) => s.student_id === studentId)?.scores?.[componentId];
    return val == null ? '' : String(val);
  };
  const setValue = (studentId: number, componentId: number, v: string) => {
    setDraft({ ...draft, [cellKey(studentId, componentId)]: v });
    setDirty(true);
  };

  const saveM = useMutation({
    mutationFn: () => {
      // only the cells you typed in: one column (e.g. CA1) can be saved now and the rest later
      const entries = Object.entries(draft).map(([k, raw]) => {
        const [sid, cid] = k.split('-').map(Number);
        return { student_id: sid, component_id: cid, score: raw.trim() === '' ? null : Number(raw) };
      });
      return postScores({
        class_id: Number(classId),
        subject_id: Number(subjectId),
        term_id: Number(termId),
        entries,
      });
    },
    onSuccess: (r: any) => {
      const d = unwrap(r);
      setDraft({});
      setDirty(false);
      setSavedAt(Date.now());
      setError(null);
      qc.invalidateQueries({ queryKey: ['scores-grid', classId, subjectId, termId] });
    },
    onError: (e: any) => {
      const msg = e?.response?.data?.message
        || (e?.response?.data?.errors && Object.values(e.response.data.errors).flat().join(' · '))
        || 'Save failed';
      setError(String(msg));
    },
  });

  const lockM = useMutation({
    mutationFn: (locked: boolean) => postScoresLock({
      class_id: Number(classId),
      subject_id: Number(subjectId),
      term_id: Number(termId),
      locked,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['scores-grid', classId, subjectId, termId] });
    },
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Lock toggle failed'),
  });

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (dirty) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // Cell-level validation
  const cellError = (studentId: number, componentId: number): string | null => {
    const col = columns.find((c: any) => c.id === componentId);
    if (!col) return null;
    const raw = valueOf(studentId, componentId);
    if (raw === '') return null;
    const n = Number(raw);
    if (!isFinite(n)) return 'Not a number';
    if (n < 0) return 'Min 0';
    if (n > col.max) return `Max ${col.max}`;
    return null;
  };

  const invalidCount = useMemo(() => {
    let n = 0;
    for (const s of students) {
      for (const c of columns) {
        if (cellError(s.student_id, c.id)) n++;
      }
    }
    return n;
  }, [students, columns, draft]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, rowIdx: number, colIdx: number) => {
    let next: { r: number; c: number } | null = null;
    if (e.key === 'Enter') { e.preventDefault(); next = { r: rowIdx + 1, c: colIdx }; }
    else if (e.key === 'ArrowDown') { e.preventDefault(); next = { r: rowIdx + 1, c: colIdx }; }
    else if (e.key === 'ArrowUp') { e.preventDefault(); next = { r: rowIdx - 1, c: colIdx }; }
    if (next && next.r >= 0 && next.r < students.length) {
      const sel = `input[data-cell="${next.r}-${next.c}"]`;
      document.querySelector<HTMLInputElement>(sel)?.focus();
    }
  };

  // Paste from Excel — distribute down the column
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>, rowIdx: number, colIdx: number) => {
    const text = e.clipboardData.getData('text');
    if (!text.includes('\t') && !text.includes('\n')) return;
    e.preventDefault();
    const values = text.trim().split(/[\r\n]+/).flatMap(line => line.split('\t'));
    const next: Record<string, string> = { ...draft };
    for (let i = 0; i < values.length; i++) {
      const r = rowIdx + i;
      if (r >= students.length) break;
      next[cellKey(students[r].student_id, columns[colIdx].id)] = values[i].trim();
    }
    setDraft(next);
    setDirty(true);
  };

  return (
    <div className="max-w-[1400px] mx-auto p-6 space-y-6">
      <div className="flex justify-between items-end">
        <div>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Scores & Results</p>
          <h1 className="text-3xl font-extrabold text-slate-900">Score Entry</h1>
          <p className="text-slate-500 text-sm mt-1">
            Enter, validate, and save scores for a class, subject, and term
          </p>
        </div>
        <div className="flex gap-2">
          {isLocked ? (
            <button onClick={async () => { if (await askConfirm('Unlock this grid? Users can edit again.')) lockM.mutate(false); }}
              className="px-3 py-2 text-sm font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 inline-flex items-center gap-1">
              <Unlock className="w-4 h-4" /> Unlock
            </button>
          ) : (
            <button onClick={async () => { if (await askConfirm('Lock this grid? Prevents further edits.')) lockM.mutate(true); }}
              disabled={!data}
              className="px-3 py-2 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 inline-flex items-center gap-1">
              <Lock className="w-4 h-4" /> Lock
            </button>
          )}
          <button onClick={() => { setNotice(null); setCbtOpen(true); }} disabled={!data || isPublished}
            title={isPublished ? 'Results are published for this class and term' : 'Bring in scores from a CBT test'}
            className="px-3 py-2 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 inline-flex items-center gap-1">
            <MonitorCheck className="w-4 h-4" /> Record from CBT
          </button>
          <button onClick={() => saveM.mutate()} disabled={!dirty || saveM.isPending || invalidCount > 0}
            className="px-4 py-2 text-sm font-semibold text-white bg-[#0f172a] rounded-lg hover:bg-slate-800 disabled:opacity-50 inline-flex items-center gap-2">
            <Save className="w-4 h-4" />
            {saveM.isPending ? 'Saving…' : dirty ? `Save ${Object.keys(draft).length} change(s)` : 'Save'}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Session *</label>
            <select value={sessionId} onChange={(e) => { setSessionId(e.target.value ? Number(e.target.value) : ''); setTermId(''); }}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
              <option value="">Choose session…</option>
              {sessions.map((s: any) => (
                <option key={s.id} value={s.id}>{s.name}{s.is_current ? ' (active)' : ''}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Term *</label>
            <select value={termId} onChange={(e) => setTermId(e.target.value ? Number(e.target.value) : '')}
              disabled={!sessionId}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white disabled:opacity-50">
              <option value="">Choose term…</option>
              {visibleTerms.map((t: any) => <option key={t.id} value={t.id}>{t.name}{t.is_current ? ' (current)' : ''}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Class *</label>
            <select value={classId} onChange={(e) => setClassId(e.target.value ? Number(e.target.value) : '')}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
              <option value="">Choose class…</option>
              {classes.map((c: any) => <option key={c.id} value={c.id}>{c.name}{c.arm ? ` · ${c.arm}` : ''}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Subject *</label>
            <select value={subjectId} onChange={(e) => setSubjectId(e.target.value ? Number(e.target.value) : '')}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
              <option value="">Choose subject…</option>
              {subjects.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        </div>
        {sessionId && !termId && (
          <p className="text-xs text-slate-500 mt-2">
            Select a term within the chosen session to load scores.
          </p>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 whitespace-pre-wrap flex items-start gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {error}
        </div>
      )}

      {isPublished && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900 flex items-start gap-2">
          <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span className="flex-1">Results for this class and term are published, so scores are frozen.{canReopen ? ' To correct or add scores, reopen them: you then compute, approve and publish again.' : ' Ask the school owner or desk officer to reopen them.'}</span>
          {canReopen && <button onClick={async () => {
            const reason = await askPrompt('Scores for this class and term can be changed again. You then compute, approve and publish again. The reason is kept in the audit log.', { title: 'Reopen results for editing', confirmLabel: 'Reopen', placeholder: 'e.g. Exam score typed wrong for two students', multiline: true });
            if (!reason) return;
            try { const r = await api.post('/school/results/reopen', { class_id: Number(classId), term_id: Number(termId), reason }); setNotice(r.data?.message ?? 'Reopened.'); qc.invalidateQueries({ queryKey: ['scores-grid', classId, subjectId, termId] }); }
            catch (e: any) { setError(e?.response?.data?.message ?? 'Could not reopen.'); }
          }} className="shrink-0 px-3 py-1.5 text-xs font-bold text-white bg-amber-600 hover:bg-amber-700 rounded-lg">Reopen for editing</button>}
        </div>
      )}
      {notice && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800 flex items-start gap-2">
          <Check className="w-4 h-4 flex-shrink-0 mt-0.5" /><span className="flex-1">{notice}</span>
          <button onClick={() => setNotice(null)} className="text-green-700 text-xs font-semibold">Dismiss</button>
        </div>
      )}
      {cbtOpen && data && (
        <CbtScoresModal classId={Number(classId)} subjectId={Number(subjectId)} termId={Number(termId)} columns={columns} students={students}
          onClose={() => setCbtOpen(false)} onDone={(m) => { setNotice(m); qc.invalidateQueries({ queryKey: ['scores-grid', classId, subjectId, termId] }); }} />
      )}

      {dirty && invalidCount === 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
          <b>{Object.keys(draft).length}</b> cell(s) changed — remember to save. Only what you changed is saved; empty columns stay empty until you fill them.
        </div>
      )}
      {invalidCount > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
          <b>{invalidCount}</b> cell(s) have validation errors. Fix before saving.
        </div>
      )}
      {savedAt && Date.now() - savedAt < 4000 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800 inline-flex items-center gap-2">
          <Check className="w-4 h-4" /> Saved
        </div>
      )}

      {/* Grid */}
      {!classId || !subjectId || !termId ? (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
          Select a class, subject, and term to load the score grid.
        </div>
      ) : scoresQ.isLoading ? (
        <div className="text-center py-8 text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> Loading scores…
        </div>
      ) : scoresQ.isError ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
          Could not load scores: {(scoresQ.error as any)?.response?.data?.message ?? 'Unknown error'}
        </div>
      ) : students.length === 0 ? (
        <div className="bg-white p-12 rounded-xl border border-slate-200 text-center">
          <p className="text-slate-600">No students are registered for this subject in this class.</p>
          <p className="text-sm text-slate-500 mt-1">Register students in Subject Registration first.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
          <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
            <p className="text-xs text-slate-600">
              <b>{data.class}</b> · {data.subject} · {data.term} · {students.length} students
            </p>
            <p className="text-xs text-slate-500">
              💡 Paste from Excel directly into any cell — it fills the column down
            </p>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-700 font-bold text-xs uppercase tracking-wider border-b-2 border-slate-300">
              <tr>
                <th className="px-3 py-3 text-left w-8">#</th>
                <th className="px-3 py-3 text-left">Student</th>
                <th className="px-3 py-3 text-left">Adm. No.</th>
                {columns.map((c: any) => (
                  <th key={c.id} className="px-2 py-3 text-center w-24">
                    <div>{c.code}</div>
                    <div className="text-[10px] font-normal text-slate-500 normal-case">max {c.max}</div>
                  </th>
                ))}
                <th className="px-3 py-3 text-right">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {students.map((s: any, rIdx: number) => {
                const total = columns.reduce((sum, c) => {
                  const v = valueOf(s.student_id, c.id);
                  const n = v === '' ? 0 : Number(v);
                  return sum + (isFinite(n) ? n : 0);
                }, 0);
                const maxTotal = columns.reduce((sum, c) => sum + Number(c.max || 0), 0);
                return (
                  <tr key={s.student_id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 text-xs text-slate-500 font-mono">{rIdx + 1}</td>
                    <td className="px-3 py-2 font-medium text-slate-900 whitespace-nowrap">{s.name}</td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-500">{s.admission_no}</td>
                    {columns.map((c: any, cIdx: number) => {
                      const err = cellError(s.student_id, c.id);
                      const raw = valueOf(s.student_id, c.id);
                      const isDirty = cellKey(s.student_id, c.id) in draft;
                      return (
                        <td key={c.id} className="px-1 py-1">
                          <input
                            type="number"
                            step="0.5"
                            min={0}
                            max={c.max}
                            value={raw}
                            data-cell={`${rIdx}-${cIdx}`}
                            onChange={(e) => setValue(s.student_id, c.id, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, rIdx, cIdx)}
                            onPaste={(e) => handlePaste(e, rIdx, cIdx)}
                            disabled={isLocked || isPublished || doneIds.has(Number(s.student_id))}
                            className={`w-full px-2 py-1.5 text-sm text-center border rounded ${
                              err ? 'border-red-400 bg-red-50' :
                              isDirty ? 'border-blue-400 bg-blue-50' :
                              'border-slate-200 focus:border-slate-500 focus:ring-1 focus:ring-slate-500'
                            } disabled:bg-slate-100 disabled:text-slate-500 outline-none`}
                            title={err ?? (doneIds.has(Number(s.student_id)) ? 'Result published: reopen the results to change it' : undefined)}
                          />
                        </td>
                      );
                    })}
                    <td className="px-3 py-2 text-right font-mono font-bold text-slate-900">
                      {total.toFixed(1)}
                      <span className="text-[10px] font-normal text-slate-400 ml-1">/ {maxTotal}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
